import axios from 'axios';
import { clearAuthStorage, emitSessionEvent, redirectToLoginPreservingNext, refreshAccessToken } from '../utils/auth';

function resolveApiBaseUrl() {
  if (typeof window !== 'undefined') {
    const host = (window.location.hostname || '').toLowerCase();
    if (host === 'agencialatamnews.com') {
      return 'https://www.agencialatamnews.com/api';
    }
  }

  const configured = (process.env.REACT_APP_API_URL || '').trim();
  if (!configured) return '/api';

  if (typeof window === 'undefined') {
    return configured;
  }

  try {
    const parsed = new URL(configured, window.location.origin);
    const apiIsLocalhost = ['localhost', '127.0.0.1'].includes(parsed.hostname);
    const browserIsLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (apiIsLocalhost && !browserIsLocalhost) {
      return '/api';
    }
    return configured;
  } catch {
    return '/api';
  }
}

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: 30000,
});

function isAuthRoute(url = '') {
  return url.includes('/auth/login') || url.includes('/auth/refresh');
}

// Interceptor para añadir token
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config || {};
    const status = error?.response?.status;
    const currentBaseUrl = (originalRequest.baseURL || api.defaults.baseURL || '').trim();

    if (!status && !originalRequest._retryBaseUrl && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(currentBaseUrl)) {
      originalRequest._retryBaseUrl = true;
      originalRequest.baseURL = '/api';
      return api(originalRequest);
    }

    if (status !== 401 || originalRequest._retry || isAuthRoute(originalRequest.url || '')) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const accessToken = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      await emitSessionEvent('forced_logout', { reason: 'refresh_failed_on_request_retry' });
      clearAuthStorage();
      redirectToLoginPreservingNext('session_expired');
      return Promise.reject(refreshError);
    }
  }
);

// Función específica para obtener tema del tenant
export const getTenantConfig = async () => {
  const response = await api.get('/config/theme');
  return response.data;
};

export default api;